import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface BeforeAfter {
  id: number;
  title: string;
  category: string;
  image: string;
}

const BeforeAfterCarousel: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const results: BeforeAfter[] = [
    {
      id: 1,
      title: "Toxina Botulínica",
      category: "Harmonização Facial",
      image: "/assets/img/row/botox.jpeg"
    },
    {
      id: 2,
      title: "Preenchimento",
      category: "Harmonização Facial",
      image: "/assets/img/row/preenchimento.jpeg"
    },
    {
      id: 3,
      title: "Lift Facial",
      category: "Rejuvenescimento",
      image: "/assets/img/row/lift facil.jpeg"
    },
    {
      id: 4,
      title: "Remodelação",
      category: "Contorno Facial",
      image: "/assets/img/row/remodelacao.jpeg"
    },
    {
      id: 5,
      title: "Pigmentação Labial",
      category: "Estética Facial",
      image: "/assets/img/row/Pingimentação Labial.jpeg"
    },
    {
      id: 6,
      title: "Terapia Capilar",
      category: "Tratamento Capilar",
      image: "/assets/img/row/Terapia Capilar.jpeg"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % results.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, results.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % results.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + results.length) % results.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section ref={elementRef as React.RefObject<HTMLElement>} className="relative py-20 md:py-28 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-[5%] w-72 h-72 bg-secondary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-[5%] w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-block px-6 py-2 bg-secondary/10 text-secondary font-semibold text-sm tracking-wider rounded-full mb-6">
            RESULTADOS REAIS
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
            Antes e Depois
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Confira a transformação dos nossos pacientes com procedimentos realizados na BotoHarmony.
          </p>
        </div>

        {/* Divider */}
        <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto mb-16"></div>

        {/* Carousel Container */}
        <div className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Main Image Display */}
          <div className="relative group">
            {/* Image Container */}
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-[16/10] w-full relative">
                {results.map((result, index) => (
                  <div
                    key={result.id}
                    className={`absolute inset-0 transition-all duration-700 ${
                      index === currentIndex 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-95 pointer-events-none'
                    }`}
                  >
                    <img
                      src={result.image}
                      alt={`${result.title} - Antes e Depois`}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    
                    {/* Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                      <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-md text-white text-xs font-bold tracking-wider rounded-full mb-3 border border-white/30">
                        {result.category}
                      </span>
                      <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                        {result.title}
                      </h3>
                      <div className="flex items-center gap-2 text-white/90">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium">Resultado Real</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Before/After Label */}
              <div className="absolute top-6 right-6 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                <span className="text-xs font-bold text-gray-700">ANTES</span>
                <div className="w-8 h-0.5 bg-gradient-to-r from-gray-400 to-secondary"></div>
                <span className="text-xs font-bold text-secondary">DEPOIS</span>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-xl flex items-center justify-center text-gray-800 hover:bg-white hover:scale-110 transition-all duration-300 opacity-0 group-hover:opacity-100 z-10"
              aria-label="Anterior"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-xl flex items-center justify-center text-gray-800 hover:bg-white hover:scale-110 transition-all duration-300 opacity-0 group-hover:opacity-100 z-10"
              aria-label="Próximo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Thumbnails Navigation */}
          <div className="mt-8 grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
            {results.map((result, index) => (
              <button
                key={result.id}
                onClick={() => goToSlide(index)}
                className={`group relative aspect-square rounded-xl overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? 'ring-4 ring-secondary shadow-xl scale-105'
                    : 'ring-2 ring-gray-200 hover:ring-secondary/50 hover:scale-105'
                }`}
              >
                <img
                  src={result.image}
                  alt={result.title}
                  className="w-full h-full object-cover"
                />
                <div className={`absolute inset-0 transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-secondary/20'
                    : 'bg-black/30 group-hover:bg-black/10'
                }`}></div>
                {index === currentIndex && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Progress Indicators */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {results.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-12 h-2 bg-gradient-to-r from-secondary to-primary'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir para slide ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-gray-600 mb-6">
            Quer resultados como estes?
          </p>
          <a
            href="/agendar"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-secondary to-primary text-white font-semibold text-sm rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Agendar Minha Avaliação
          </a>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterCarousel;
